/*
 * Byte-identical in Cortex and Quantum.
 *
 * Readable strategy microsite addresses:
 *
 *   /campaign-strategy/{advertiser-slug}/{campaign-slug}
 *   /campaign-strategy/the-action-bible/christmas-ctv-2026
 *
 * The backend assigns every address once and resolves it
 * (GET /api/media-campaigns/strategy-microsite-path/:advertiser/:campaign);
 * the campaign id never appears in the address, and same-named campaigns are
 * told apart by the backend's numbering, never guessed here. This module only
 * parses, recognises and compares paths.
 *
 * A one-segment path (/campaign-strategy/{identifier}) is a legacy link — the
 * old numeric id or combined name slug. It still opens, and the page replaces
 * it with the readable address as soon as the strategy loads.
 */

export const CAMPAIGN_STRATEGY_MICROSITE_ROUTE = "/campaign-strategy";

const SLUG = "[a-z0-9]+(?:-[a-z0-9]+)*";
const READABLE_PATH = new RegExp(`^/campaign-strategy/${SLUG}/${SLUG}$`);

const decodeSegment = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const parseCampaignStrategyMicrositePath = (pathname = "") => {
  const match = /^\/campaign-strategy\/([^/?#]+)(?:\/([^/?#]+))?\/?$/.exec(String(pathname || ""));
  if (!match) return null;
  if (match[2]) {
    return {
      advertiserSlug: decodeSegment(match[1]),
      campaignSlug: decodeSegment(match[2]),
      identifier: "",
    };
  }
  return { advertiserSlug: "", campaignSlug: "", identifier: decodeSegment(match[1]) };
};

export const isCampaignStrategyMicrositePath = (pathname = "") =>
  Boolean(parseCampaignStrategyMicrositePath(pathname));

export const isReadableCampaignStrategyMicrositePath = (value) => READABLE_PATH.test(String(value || ""));

/* The readable address a campaign payload carries, or "" (never an id path). */
export const readCampaignStrategyMicrositeAddress = (campaign) => {
  const path = campaign?.strategyMicrositePath ?? campaign?.strategy_microsite_path ?? "";
  return isReadableCampaignStrategyMicrositePath(path) ? path : "";
};

/*
 * Does a stored "return to the planner" state belong to the microsite being
 * viewed? It does when it names the same readable address, the same resolved
 * campaign, or — for a legacy link — the same identifier; a state that names
 * nothing is accepted, one that names something else is not.
 */
export const micrositeReturnStateMatches = (stored, { pathname = "", campaignId = "" } = {}) => {
  if (!stored || typeof stored !== "object") return false;
  const storedPath = String(stored.micrositePath || "").trim();
  const storedCampaignId = String(stored.campaignId || "").trim();
  const storedSlug = String(stored.micrositeSlug || stored.publicSlug || stored.strategyMicrositeSlug || "").trim();
  if (!storedPath && !storedCampaignId && !storedSlug) return true;
  const resolvedCampaignId = String(campaignId || "").trim();
  if (resolvedCampaignId && storedCampaignId && resolvedCampaignId === storedCampaignId) return true;
  const path = String(pathname || "").replace(/\/+$/, "");
  if (storedPath && path && storedPath === path) return true;
  const parsed = parseCampaignStrategyMicrositePath(path);
  if (parsed?.identifier) {
    return parsed.identifier === storedCampaignId || (storedSlug !== "" && parsed.identifier === storedSlug);
  }
  return false;
};

/*
 * A legacy link replaced by the readable address takes the stored "return to
 * the planner" state with it, so reloading the readable address still finds
 * it. Only a state that belongs to the page being viewed is carried.
 */
export const rememberMicrositeReturnAddress = (storageKey, micrositePath, viewing = {}) => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || "{}");
    if (!stored || typeof stored !== "object" || !Object.keys(stored).length) return;
    if (!micrositeReturnStateMatches(stored, viewing)) return;
    sessionStorage.setItem(storageKey, JSON.stringify({ ...stored, micrositePath }));
  } catch {
    // Storage unavailable: the route state still carries the return target.
  }
};
